import { LAYOUT_CONSTANTS } from "@src/constants";
import { useApplicationContext } from "@src/context/ApplicationContext";
import { useResolvedPageLabels } from "@src/hooks/useResolvedPageLabels";
import ClientLogsService from "@src/services/client-logs.service";
import logger from "@src/utils/logger";
import { styled } from "@uhg-abyss/web/tools/styled";
import { Button } from "@uhg-abyss/web/ui/Button";
import { Divider } from "@uhg-abyss/web/ui/Divider";
import { ModalDialog } from "@uhg-abyss/web/ui/ModalDialog";
import { Text } from "@uhg-abyss/web/ui/Text";
import { useEffect } from "react";

const ModalFooterContainer = styled("div", {
  display: "flex",
  justifyContent: "flex-end",
  width: "100%",
  paddingTop: "16px 0 0 0",
});

const MemberIDImageWrapper = styled("div", {
  position: "relative",
  display: "inline-block",
  margin: "0 auto",
});

const MemberIDImage = styled("img", {
  width: "300px",
  height: "188px",
  marginTop: "16px",
  borderRadius: "8px",
  position: "relative",
  zIndex: 0,
  border: "1px solid $app.color.black",
});

const MemberIDOverlay = styled("div", {
  position: "absolute",
  top: 0,
  left: 0,
  width: "300px",
  height: "188px",
  marginTop: "16px",
  borderRadius: "8px",
  backgroundColor: "rgba(255, 255, 255, 0.75)",
  zIndex: 1,
});

const MemberIDAnnotationBox = styled("div", {
  position: "absolute",
  backgroundColor: "$app.color.surface.container.secondary",
  textAlign: "left",
  marginTop: "50px",
  border: "3px solid $app.color.find.member.id.modal.annotation.border",
  borderRadius: "4px",
});

const MemberIDAnnotationText = styled("p", {
  fontWeight: "500",
  lineHeight: "1",
  margin: "0 2px",
});

const MemberIDLabel = styled("span", {
  fontSize: "11px",
  color: "$app.color.find.member.id.modal.member.id.label",
});

const MemberIDValue = styled("span", {
  fontSize: "13px",
  color: "$app.color.find.member.id.modal.member.id.value",
});

export const FindMemberID = ({ isOpen, setIsOpen }) => {
  const { data } = useApplicationContext();
  const labels = useResolvedPageLabels("findMemberIdModal");

  useEffect(() => {
    const hasSeenModal = localStorage.getItem("obc-find-member-id-modal");
    logger.info({
      text: `hasSeenModal:`,
      value: hasSeenModal,
      location: "FindMemberIDModal",
    });
    if (hasSeenModal === "true") {
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  }, [setIsOpen]);

  const handleClose = () => {
    setIsOpen(false);
    localStorage.setItem("obc-find-member-id-modal", "false"); // Persist dismissal state
  };

  const modalFooter = (
    <ModalFooterContainer>
      <Button
        data-testid="test-id-i-understand-button"
        id="obc-find-member-id-modal-got-it-btn"
        data-obc-label="Find Member ID Modal Got It Button"
        onClick={handleClose}
      >
        Got it
      </Button>
    </ModalFooterContainer>
  );

  const ModalDialogStyle = styled(ModalDialog, {
    ".abyss-modal-base-header-container": {
      display: "flex",
      flexDirection: "column",
      lineHeight: "0",
      width: "fit-content",
      alignSelf: "center",
      padding: "20px 20px 0 20px",
      ".abyss-modal-base-close-button": {
        justifyContent: "flex-end",
        alignSelf: "flex-end",
        color: "$app.color.find.member.id.modal.close.button !important",
        ".abyss-icon-symbol": {
          color: "$app.color.find.member.id.modal.close.button !important",
        },
      },
    },
    ".abyss-modal-base-header": {
      fontSize: "32px !important",
      fontWeight: "700",
      padding: "0",
    },
    ".abyss-modal-base-body": {
      padding: "0px",
      lineHeight: "0",
      rowGap: "5px !important",
      ".abyss-text-root": {
        padding: "0 20px 0 20px",
      },
    },
    ".abyss-modal-base-footer": {
      padding: "10px 20px 20px 20px",
    },
  });

  return (
    <ModalDialogStyle
      data-testid="test-id-find-member-id-modal"
      header={labels.headingText}
      isOpen={isOpen}
      onClose={() => {
        ClientLogsService.logClick(
          data.userUuid,
          "FindMemberIDModal",
          "obc-find-member-id-modal-close-btn",
          "Find Member ID Modal Close Button",
        );
        handleClose();
      }}
      footer={modalFooter}
      size={LAYOUT_CONSTANTS.modalDialogWidth}
      closeOnClickOutside={false}
    >
      <Divider
        color="$app.color.find.member.id.modal.divider"
        height={"2px"}
        css={{
          padding: "0",
        }}
      />
      <Text>{labels.bodyText}</Text>
      <MemberIDImageWrapper>
        <MemberIDImage src="/member-id-example.png" alt="Member ID Example" />
        <MemberIDOverlay>
          <MemberIDAnnotationBox>
            <MemberIDAnnotationText>
              <MemberIDLabel>Member ID: </MemberIDLabel>
              <MemberIDValue>1234567890</MemberIDValue>
            </MemberIDAnnotationText>
          </MemberIDAnnotationBox>
        </MemberIDOverlay>
      </MemberIDImageWrapper>
    </ModalDialogStyle>
  );
};
